import {
  BarChart3,
  Compass,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  Handshake,
  Home,
  Inbox,
  Kanban,
  LogOut,
  Search,
  Settings,
  User,
  PhoneCall,
  TrendingUp,
  UsersRound,
  Wallet,
  BookOpen,
} from "lucide-react";
import { useGetIdentity, useLogout, useStore, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { AjustesPage } from "../ajustes/AjustesPage";
import { PaginaPerfil } from "../ajustes/PaginaPerfil";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useCon36 } from "../datos/useFiltroEtapas";
import { useMiRol } from "../datos/useMiRol";
import { abrirGuia } from "../guia/estado";
import { papelDe } from "../guia/pasos";
import { useResponsables } from "../datos/useResponsables";
import { AvatarUsuario } from "../usuarios/FotoUsuario";
import { ComisionesPage } from "../equipo/ComisionesPage";
import { MisComisionesPage } from "../equipo/MisComisionesPage";
import { EquipoPage } from "../equipo/EquipoPage";
import { SinAsignarPage } from "../equipo/SinAsignarPage";
import { ColaboracionesPage } from "../equipo/ColaboracionesPage";
import { InformeMensual } from "../panel/InformeMensual";
import { ResumenPage } from "../resumen/ResumenPage";
import { useResumenDisponible } from "../resumen/useResumenDisponible";
import { PipelinePage } from "../pipeline/PipelinePage";
import { abrirBuscador } from "../negocios/buscador";
import { Isotipo } from "../tema/Isotipo";
import { iniciales } from "../negocios/formato";

type Icono = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type Entrada = {
  clave: string;
  ruta: string;
  patron: string;
  Icono: Icono;
  /** Solo administradores. */
  admin?: boolean;
  /** Reservado para la FASE 5: no se muestra todavía. */
  oculto?: boolean;
  /** Solo si la base ya lo tiene (28d: resumen de llamadas). */
  requiereResumen?: boolean;
  /** 36: solo para quien vende (no administradores ni consultores) y con la 36 en la base. */
  vende?: boolean;
  /** 40: para quien cobra comisión (setters y consultores), con la 36 en la base. */
  cobra?: boolean;
  /** 39: nunca para un consultor. */
  noConsultor?: boolean;
};

/** Menú principal (SPEC.md §2–§6). Nevora queda preparado para FASE 5. */
const PRINCIPAL: Entrada[] = [
  { clave: "nevora.nav.inicio", ruta: "/", patron: "/", Icono: Home },
  {
    clave: "nevora.nav.leads",
    ruta: "/negocios",
    patron: "/negocios/*",
    // Auditoría 30/10/2026: un icono propio (antes, casi el mismo que Equipo).
    Icono: PhoneCall,
  },
  // 43 y auditoría 30/10/2026: las propuestas de colaboración de la web, junto a Leads (no
  // son del equipo comercial). Solo Jan y Robert.
  {
    clave: "nevora.nav.colaboraciones",
    ruta: ColaboracionesPage.path,
    patron: `${ColaboracionesPage.path}/*`,
    Icono: Handshake,
    admin: true,
  },
  {
    clave: "nevora.nav.pipeline",
    ruta: PipelinePage.path,
    patron: `${PipelinePage.path}/*`,
    Icono: Kanban,
  },
  {
    clave: "nevora.nav.agenda",
    ruta: "/agenda",
    patron: "/agenda/*",
    Icono: CalendarDays,
  },
  {
    // 28d (Robert, 15/10/2026): para todos, también los setters.
    clave: "nevora.nav.resumen",
    ruta: ResumenPage.path,
    patron: `${ResumenPage.path}/*`,
    Icono: BarChart3,
    requiereResumen: true,
    // 39 (Robert, 23/10/2026): el consultor no llama; el resumen es de quien llama.
    noConsultor: true,
  },
  {
    // 47 (Robert, 31/10/2026): mes a mes, qué ha funcionado y qué no.
    clave: "nevora.nav.informe",
    ruta: InformeMensual.path,
    patron: `${InformeMensual.path}/*`,
    Icono: TrendingUp,
    noConsultor: true,
  },
  {
    clave: "nevora.nav.mis_comisiones",
    ruta: MisComisionesPage.path,
    patron: `${MisComisionesPage.path}/*`,
    Icono: Wallet,
    cobra: true,
  },
  {
    clave: "nevora.nav.nevora",
    ruta: "/nevora",
    patron: "/nevora/*",
    Icono: BookOpen,
    oculto: true,
  },
];

/** Gestión del equipo comercial (S9-S10): solo Jan y Robert. */
const EQUIPO: Entrada[] = [
  {
    clave: "nevora.nav.sin_asignar",
    ruta: SinAsignarPage.path,
    patron: `${SinAsignarPage.path}/*`,
    Icono: Inbox,
    admin: true,
  },
  {
    clave: "nevora.nav.equipo",
    ruta: EquipoPage.path,
    patron: `${EquipoPage.path}/*`,
    Icono: UsersRound,
    admin: true,
  },
  {
    clave: "nevora.nav.comisiones",
    ruta: ComisionesPage.path,
    patron: `${ComisionesPage.path}/*`,
    Icono: Wallet,
    admin: true,
  },
];

// Auditoría 30/10/2026: «Importar CSV» pasa a ser un botón de Leads (se usa de vez en cuando).
const SECUNDARIO: Entrada[] = [
  {
    clave: "nevora.nav.ajustes",
    ruta: AjustesPage.path,
    patron: `${AjustesPage.path}/*`,
    Icono: Settings,
    admin: true,
  },
];

export const ANCHO_BARRA = "16rem"; // 256 px, como la mayoría de CRM (petición de Robert, 25/09/2026)
export const ANCHO_BARRA_PLEGADA = "4rem";

/**
 * Barra lateral fija y plegable (referencia: Pipedrive, HubSpot, Attio). El estado
 * plegada/desplegada se recuerda por usuario en el store de ra-core (localStorage).
 */
export const BarraLateral = () => {
  const translate = useTranslate();
  const location = useLocation();
  const esAdmin = useEsAdmin();
  const resumenDisponible = useResumenDisponible();
  const { vende, esConsultor } = useMiRol();
  const con36 = useCon36();
  const [plegada, setPlegada] = useStore<boolean>(
    "nevora.barra.plegada",
    false,
  );

  const activa = (e: Entrada) =>
    e.patron === "/"
      ? location.pathname === "/"
      : !!matchPath(e.patron, location.pathname);

  const Elemento = ({ e }: { e: Entrada }) => (
    <Link
      to={e.ruta}
      data-guia={`nav-${e.clave.split(".").pop()}`}
      title={plegada ? translate(e.clave) : undefined}
      className={cn(
        // Rediseño (30/10/2026): lo activo, en blanco con sombra suave sobre la barra tintada.
        "flex items-center gap-3 rounded-lg px-2.5 py-[7px] text-sm font-medium transition-[background-color,color,box-shadow]",
        activa(e)
          ? "bg-sidebar-accent text-foreground shadow-tarjeta ring-1 ring-sidebar-border"
          : "text-muted-foreground hover:bg-foreground/[0.045] hover:text-foreground",
        plegada && "justify-center px-0",
      )}
    >
      <e.Icono
        className={cn("size-[18px] shrink-0", activa(e) && "text-[#3D8BD4]")}
        strokeWidth={activa(e) ? 2.1 : 1.8}
      />
      {!plegada ? <span className="truncate">{translate(e.clave)}</span> : null}
    </Link>
  );

  return (
    <aside
      aria-label={translate("crm.navigation.label")}
      className="fixed inset-y-0 left-0 z-40 flex flex-col border-r border-sidebar-border bg-sidebar"
      style={{ width: plegada ? ANCHO_BARRA_PLEGADA : ANCHO_BARRA }}
    >
      <Link
        to="/"
        className={cn(
          "flex items-center gap-2.5 h-16 px-4 no-underline text-foreground",
          plegada && "justify-center px-0",
        )}
      >
        <Isotipo className="h-7" />
        {!plegada ? (
          <span className="font-marca uppercase tracking-[0.12em] text-base font-medium leading-none">
            Nevora{" "}
            <span className="font-normal text-muted-foreground">CRM</span>
          </span>
        ) : null}
      </Link>

      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5">
        <button
          type="button"
          data-guia="buscar"
          onClick={abrirBuscador}
          title={plegada ? translate("nevora.buscador.titulo") : undefined}
          className={cn(
            "mb-3 flex w-full items-center gap-3 rounded-lg bg-sidebar-accent/80 px-2.5 py-[7px] text-sm text-muted-foreground shadow-tarjeta ring-1 ring-sidebar-border transition-colors hover:bg-sidebar-accent hover:text-foreground",
            plegada && "justify-center px-0",
          )}
        >
          <Search className="size-[18px] shrink-0" />
          {!plegada ? (
            <>
              <span className="flex-1 truncate text-left">
                {translate("nevora.buscador.titulo")}
              </span>
              <kbd className="rounded border px-1.5 text-[11px] font-medium">
                Ctrl K
              </kbd>
            </>
          ) : null}
        </button>
        {PRINCIPAL.filter(
          (e) =>
            !e.oculto &&
            (!e.admin || esAdmin) &&
            (!e.requiereResumen || resumenDisponible) &&
            (!e.vende || (vende && con36)) &&
            (!e.cobra || ((vende || esConsultor) && con36)) &&
            (!e.noConsultor || !esConsultor),
        ).map((e) => (
          <Elemento key={e.ruta} e={e} />
        ))}
        {esAdmin ? (
          <div className="pt-5 space-y-0.5">
            {!plegada ? (
              <p className="etiqueta px-2.5 pb-1.5">
                {translate("nevora.nav.grupo_equipo")}
              </p>
            ) : null}
            {EQUIPO.map((e) => (
              <Elemento key={e.ruta} e={e} />
            ))}
          </div>
        ) : null}
      </nav>

      <div className="px-3 py-3 space-y-0.5 border-t border-sidebar-border">
        {SECUNDARIO.filter((e) => !e.admin || esAdmin).map((e) => (
          <Elemento key={e.ruta} e={e} />
        ))}
        <MenuUsuario plegada={plegada} />
        {/* Auditoría 30/10: sin botón «Refrescar» (los datos se actualizan solos). */}
        <div
          className={cn(
            "flex items-center pt-1",
            plegada ? "justify-center" : "justify-end px-1",
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground"
            aria-label={translate(
              plegada ? "ra.action.open_menu" : "ra.action.close_menu",
            )}
            onClick={() => setPlegada(!plegada)}
          >
            {plegada ? (
              <ChevronsRight className="size-4" />
            ) : (
              <ChevronsLeft className="size-4" />
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
};

const MenuUsuario = ({ plegada }: { plegada: boolean }) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const logout = useLogout();
  const nombre = identity?.fullName ?? "";
  const { foto } = useResponsables();
  const rol = useMiRol();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "w-full flex items-center gap-3 rounded-lg px-2.5 py-[7px] text-sm hover:bg-foreground/[0.045] transition-colors",
            plegada && "justify-center px-0",
          )}
          aria-label={translate("ra.auth.user_menu")}
          data-guia="usuario"
        >
          {foto(identity?.id) ? (
            <AvatarUsuario
              nombre={nombre}
              foto={foto(identity?.id)}
              className="size-7 text-xs"
            />
          ) : (
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#0A0A0A] text-[#FAFAF7] text-xs font-semibold">
              {iniciales(nombre) || <User className="size-4" />}
            </span>
          )}
          {!plegada ? (
            <span className="truncate text-left font-medium">{nombre}</span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel className="font-normal truncate">
          {nombre}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={PaginaPerfil.path} className="flex items-center gap-2">
            <User className="size-4" />
            {translate("nevora.nav.perfil")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => abrirGuia(papelDe(rol))}
          className="cursor-pointer"
        >
          <Compass className="size-4" />
          {translate("nevora.guia.ver")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => logout()} className="cursor-pointer">
          <LogOut className="size-4" />
          {translate("ra.auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
